import { storage } from './utils';
import {
  ATTRIBUTION_KEYS,
  ATTRIBUTION_TTL_MS,
  cleanAttribution,
  externalReferrer,
  readAttributionCookie,
  writeAttributionCookie,
} from '../../../shared/attribution-cookie';

const KEY = 'cs_attribution';
interface Attribution {
  referral: string | null;
  source: string | null;
  ts: number;
  values?: Record<string, string>;
}

function savedAttribution(): Attribution | null {
  const shared = readAttributionCookie(document, window.location);
  if (shared)
    return {
      referral: shared.values.ref || shared.values.referral || null,
      source: sourceOf(shared.values),
      ts: shared.ts,
      values: shared.values,
    };
  const local = storage.get<Attribution | null>(KEY, null);
  if (!local || !Number.isFinite(local.ts) || Date.now() - local.ts >= ATTRIBUTION_TTL_MS) return null;
  // Older saves may hold a sign-in host (accounts.google.com, *.supabase.co) as the source.
  const values = cleanAttribution({ ...(local.values || {}), ...(local.source && !local.values ? { referrer: local.source } : {}) });
  if (!Object.keys(values).length && !local.referral) return null;
  return { referral: local.referral, source: sourceOf(values), ts: local.ts, values };
}

/** The marketing source: an explicit `source`, then `utm_source`, then the referring site. */
function sourceOf(values: Record<string, string>): string | null {
  return values.source || values.utm_source || values.referrer || null;
}

/** Share referral across production subdomains; retain local storage and URL fallback. */
export function captureAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    const referral = params.get('ref') || params.get('referral');
    const fresh = savedAttribution();
    const current = cleanAttribution(
      Object.fromEntries(
        ATTRIBUTION_KEYS.filter((key) => params.get(key)).map((key) => [key, params.get(key)!]),
      ),
    );
    let next = fresh;
    if (Object.keys(current).length) {
      // A new campaign replaces the old one; a page opened without tags keeps it.
      const values = { ...(fresh?.values || {}), ...current };
      const changed = Object.entries(current).some(([key, value]) => fresh?.values?.[key] !== value);
      next = {
        values,
        referral: referral || fresh?.referral || null,
        source: sourceOf(values),
        ts: fresh && !changed ? fresh.ts : Date.now(),
      };
    } else if (!fresh) {
      // First visit with no tags: remember the external site that sent it, never a sign-in page.
      const referrer = externalReferrer(document.referrer);
      if (referrer) {
        const values = { referrer, landing: (window.location.pathname || '/').slice(0, 200) };
        next = { referral: null, source: referrer, ts: Date.now(), values };
      }
    }
    if (!next) {
      storage.remove(KEY);
      return;
    }
    storage.set(KEY, next);
    const shared = readAttributionCookie(document, window.location);
    writeAttributionCookie(
      {
        ts: next.ts,
        values: {
          ...(shared?.values || {}),
          ...(next.values || {}),
          ...(next.referral ? { ref: next.referral } : {}),
          ...(next.source ? { source: next.source } : {}),
        },
      },
      document,
      window.location,
    );
  } catch {
    /* Attribution is best-effort. */
  }
}

/** Read the shared cookie again at signup, including referrals from another open tab. */
export function getAttribution(): {
  referral: string | null;
  source: string | null;
  attribution?: Record<string, string>;
} {
  const saved = savedAttribution();
  return {
    referral: saved?.referral || null,
    source: saved?.source || null,
    ...(saved?.values ? { attribution: saved.values } : {}),
  };
}
